// src/components/Register.js
import React, { useState, useEffect, useRef } from 'react'
import Header from './Header'
import '../styles/Register.css'
import '../styles/Transport.css'
// Import icons from react-icons
import { FaInfoCircle, FaEye, FaEyeSlash, FaUpload } from 'react-icons/fa'
import axios from 'axios'
import { T } from './Translation'
import { useLanguage } from '../context/LanguageContext'
import { translations } from '../translations/translations'
import PayMe3DSCheckout from './Payme/PayMe3DSCheckout'
// Define BASE_URL for API requests
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'

// Plan configurations with prices
const PLANS = {
  basic: {
    name: 'basic',
    priceILS: 229
  },
  premium: {
    name: 'premium',
    priceILS: 259
  },
};
// Component for input with tooltip
const InputWithTooltip = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  tooltip,
  prefix,
  min,
  max,
  translationKey,
  tooltipKey,
  error,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const toggleTooltip = () => {
    setIsTooltipVisible(!isTooltipVisible)
  }

  return (
    <div className="input-tooltip-wrapper">
      {label && <label className="input-label">{label}</label>}
      <div className="label-tooltip">
        {prefix ? (
          <div className="input-with-prefix">
            <span className="input-prefix">{prefix}</span>
            <input
              type={type}
              placeholder={
                translationKey ? <T k={translationKey} /> : placeholder
              }
              value={value}
              onChange={onChange}
              aria-label={translationKey ? <T k={translationKey} /> : label}
              className="input-with-prefix-field"
              min={min}
              max={max}
            />
            {(tooltip || tooltipKey) && (
              <div className="tooltip-container tooltip-container--inline">
                <FaInfoCircle className="info-icon" onClick={toggleTooltip} />
                <span className={`tooltip-text ${isTooltipVisible ? 'visible' : ''}`}>
                  {tooltipKey ? <T k={tooltipKey} /> : tooltip}
                </span>
              </div>
            )}
          </div>
        ) : (
          <input
            type={type}
            placeholder={
              translationKey ? <T k={translationKey} /> : placeholder
            }
            value={value}
            onChange={onChange}
            aria-label={translationKey ? <T k={translationKey} /> : label}
            min={min}
            max={max}
          />
        )}
        {!prefix && (tooltip || tooltipKey) && (
          <div className="tooltip-container">
            <FaInfoCircle className="info-icon" onClick={toggleTooltip} />
            <span className={`tooltip-text ${isTooltipVisible ? 'visible' : ''}`}>
              {tooltipKey ? <T k={tooltipKey} /> : tooltip}
            </span>
          </div>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  )
}

// Component for textarea with tooltip and label
const TextareaWithTooltip = ({
  label,
  placeholder,
  value,
  onChange,
  tooltip,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const toggleTooltip = () => {
    setIsTooltipVisible(!isTooltipVisible)
  }

  return (
    <div className="message-input-container">
      <label className="input-label">{label}</label>
      <div className="input-tooltip-wrapper">
        <div className="label-tooltip">
          <textarea
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            aria-label={label}
          />
          <div className="tooltip-container">
            <FaInfoCircle className="info-icon" onClick={toggleTooltip} />
            <span
              className={`tooltip-text ${isTooltipVisible ? 'visible' : ''}`}
            >
              {tooltip}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Verification code input component
const VerificationCodeInput = ({ length = 6, onComplete }) => {
  const [code, setCode] = useState(Array(length).fill(''))
  const inputs = useRef([])

  const handleChange = (e, index) => {
    const value = e.target.value
    // Only allow numeric input
    if (/[^0-9]/.test(value)) return

    // Update the code array with the new value
    const newCode = [...code]
    newCode[index] = value.substring(0, 1)
    setCode(newCode)

    // Build the complete code
    const completeCode = newCode.join('')

    // If we have a digit and it's not the last input, focus next input
    if (value && index < length - 1) {
      inputs.current[index + 1].focus()
    }

    // If we've filled all inputs, trigger the onComplete callback
    if (completeCode.length === length) {
      onComplete(completeCode)
    }
  }

  const handleKeyDown = (e, index) => {
    // If backspace is pressed and current input is empty, focus previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1].focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').trim()
    if (!/^\d+$/.test(pastedData)) return // Only allow numeric pastes

    const newCode = [...code]
    for (let i = 0; i < Math.min(length, pastedData.length); i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)

    // Focus the appropriate input box after paste
    const focusIndex = Math.min(length - 1, pastedData.length)
    inputs.current[focusIndex].focus()

    // If we've filled all inputs with the paste, trigger onComplete
    if (pastedData.length >= length) {
      onComplete(pastedData.substring(0, length))
    }
  }

  return (
    <div className="verification-code-container">
      {code.map((digit, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={index === 0 ? handlePaste : undefined}
          ref={(el) => (inputs.current[index] = el)}
          className="verification-code-input"
          autoFocus={index === 0}
        />
      ))}
    </div>
  )
}

function Register() {
  // Add navigation hook
  const { language } = useLanguage()
  const latest = useRef({}) // never triggers re-renders
  useEffect(() => {
    latest.current = {
      step,
      wifeName,
      husbandName,
      email,
      phone,
      eventType,
      mapLink,
      eventDate,
      eventTime,
      bitLink,
      payboxLink,
      organizeTransport,
      transportStops,
      paymentMethod,
      username,
      password,
      confirmPassword,
      rsvpText,
      tomorrowMessage,
      rsvpTextSecond,
      afterWeddingText,
      selectedPlan,
    }
  }) // runs after every render

  // Keep track of which step we're on

  // Form fields
  const draft = (() => {
    try {
      const raw = localStorage.getItem('wizard-draft')
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  })()

  // 2️⃣ initialise each piece of state with the draft’s value or a default
  const [step, setStep] = useState(draft.step || 1)
  const [wifeName, setWifeName] = useState(draft.wifeName || '')
  const [husbandName, setHusbandName] = useState(draft.husbandName || '')
  const [email, setEmail] = useState(draft.email || '')
  const [phone, setPhone] = useState(draft.phone || '')
  const [eventType, setEventType] = useState(draft.eventType || 'Wedding')
  const [mapLink, setMapLink] = useState(draft.mapLink || '')
  const [eventDate, setEventDate] = useState(draft.eventDate || '')
  const [eventTime, setEventTime] = useState(draft.eventTime || '')
  const [bitLink, setBitLink] = useState(draft.bitLink || '')
  const [payboxLink, setPayboxLink] = useState(draft.payboxLink || '')
  const [organizeTransport, setOrganizeTransport] = useState(
    draft.organizeTransport ?? false
  )
  const [transportStops, setTransportStops] = useState(
    draft.transportStops || []
  )
  const [paymentMethod, setPaymentMethod] = useState(draft.paymentMethod || '')
  const [username, setUsername] = useState(draft.username || '')
  const [password, setPassword] = useState(draft.password || '')
  const [confirmPassword, setConfirmPassword] = useState(
    draft.confirmPassword || ''
  )

  const [invitationImage, setInvitationImage] = useState(null)

  const [newStop, setNewStop] = useState('')

  const [showPassword, setShowPassword] = useState(false)
  /* ----- NEW ----- */
  const [rsvpText, setRsvpText] = useState(
    draft.rsvpText || 'היי! נשמח לאשר את הגעתכם לחתונתנו דרך הקישור: '
  )
  const [tomorrowMessage, setTomorrowMessage] = useState(
    draft.tomorrowMessage || 'היי! רק רצינו להזכיר לכם את החתונה מחר. נשמח לראותכם!'
  )
  const [rsvpTextSecond, setRsvpTextSecond] = useState(
    draft.rsvpTextSecond || 'תזכורת אחרונה! אנא אשרו הגעה בלחיצה על הקישור: '
  )
  const [afterWeddingText, setAfterWeddingText] = useState(
    draft.afterWeddingText ||
      'תודה שחגגתם איתנו! הייתם חלק בלתי נשכח מהיום המיוחד שלנו ❤️'
  )

  // Field-level validation errors and step-validation loading state
  const [fieldErrors, setFieldErrors] = useState({})
  const [validating, setValidating] = useState(false)

  const setFieldError = (field, msg) =>
    setFieldErrors((prev) => ({ ...prev, [field]: msg }))
  const clearFieldError = (field) =>
    setFieldErrors((prev) => { const next = { ...prev }; delete next[field]; return next })

  // Additional state variables
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [imageUploadError, setImageUploadError] = useState('')
  const [couponCode, setCouponCode] = useState('')
  const [couponValid, setCouponValid] = useState(false)
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState('')


  // ----new----
  // Payment iframe state 
  const [paymentUrl, setPaymentUrl] = useState('')
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  // ----new----

  // Plan selection and payment method state
  const [selectedPlan, setSelectedPlan] = useState(draft.selectedPlan || '')
  const [paymentChoice, setPaymentChoice] = useState('') // 'coupon' or 'card'

  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [remainingTime, setRemainingTime] = useState(60)
  const [canResend, setCanResend] = useState(false)

  // New state for tracking verification success
  const [verificationSuccess, setVerificationSuccess] = useState(false)

  // Load saved form data on component mount
  useEffect(() => {
    setError('') // Clear any existing errors on mount
    const save = () => {
      const data = latest.current
      // the “hasData” guard you already use – keep or drop as you like
      const hasData = Object.values(data).some((v) => {
        if (Array.isArray(v)) return v.length
        if (typeof v === 'boolean') return v
        if (typeof v === 'number') return v !== 1 // step starts at 1
        return v && v.toString().trim() !== ''
      })
      if (hasData) {
        localStorage.setItem('wizard-draft', JSON.stringify(data))
      }
    }

    /* page refresh / tab close */
    window.addEventListener('beforeunload', save)

    /* cleanup = componentWillUnmount */
    return () => {
      save() // route change / SPA unmount
      window.removeEventListener('beforeunload', save)
    }
  }, [])

  // Timer effect for resend countdown
  useEffect(() => {
    let timer
    if (showVerificationModal && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [showVerificationModal, remainingTime])

  // Normalize phone to full international format (+972XXXXXXXXX)
  const fullPhone = (p) => {
    const digits = (p || '').trim().replace(/\D/g, '')
    if (digits.startsWith('972')) return `+${digits}`
    if (digits.startsWith('5') && digits.length === 9) return `+972${digits}`
    return `+${digits}`
  }

  // Validation states (mock)
  // const isStep1Valid = wifeName && husbandName && email && phone && eventType
  // const isStep2Valid = mapLink && eventDate && eventTime
    const isStep1Valid = true // Optional fields
  const isStep2Valid = true 
  const isStep3Valid = true // Optional fields
  const isStep4Valid = true // messages – no required field
  const isStep5Valid = true // transport - optional
  // const isStep6Valid =
  //   username && password && confirmPassword && password === confirmPassword
  const isStep6Valid = true
  // Helper to go to the next or previous step
  const nextStep = () => setStep(step + 1)
  const prevStep = () => setStep(step - 1)

  // Build saleInfo for coupon payment (50 ILS)
  const buildCouponSaleInfo = () => {
    const wifeNames = wifeName.trim().split(' ')
    const husbandNames = husbandName.trim().split(' ')
    const payerFirstName = wifeNames[0] || husbandNames[0] || 'Guest'
    const payerLastName = wifeNames.slice(1).join(' ') || husbandNames.slice(1).join(' ') || ''
    const productName = language === 'he' ? 'Invitly - מחיר מיוחד בקופון' : 'Invitly - Coupon Special Price'
    return {
      seller_payme_id: process.env.REACT_APP_PAYME_SELLER_ID,
      sale_price: 5000, // 50 ILS in agorot
      currency: 'ILS',
      product_name: productName,
      payerFirstName,
      payerLastName,
      payerEmail: email || 'guest@invitly.com',
      payerPhone: phone ? fullPhone(phone) : '+972500000000',
      total: {
        amount: { value: '50.00', currency: 'ILS' },
        label: productName,
      },
    }
  }

  // Build saleInfo dynamically based on user data and selected plan
  const buildSaleInfo = () => {
    if (!selectedPlan) return null
    
    const plan = PLANS[selectedPlan]
    const priceInAgorot = plan.priceILS * 100 // PayMe expects price in agorot (cents)
    const priceFormatted = plan.priceILS.toFixed(2)
    
    // Parse names
    const wifeNames = wifeName.trim().split(' ')
    const husbandNames = husbandName.trim().split(' ')
    const payerFirstName = wifeNames[0] || husbandNames[0] || 'Guest'
    const payerLastName = wifeNames.slice(1).join(' ') || husbandNames.slice(1).join(' ') || ''
    
    const productName = language === 'he' 
      ? `תוכנית ${plan.name === 'basic' ? 'בסיסית' : 'פרימיום'} - Invitly`
      : `${plan.name.charAt(0).toUpperCase() + plan.name.slice(1)} Plan - Invitly`
    
    return {
      seller_payme_id: process.env.REACT_APP_PAYME_SELLER_ID,
      sale_price: priceInAgorot,
      currency: 'ILS',
      product_name: productName,
      payerFirstName: payerFirstName,
      payerLastName: payerLastName,
      payerEmail: email || 'guest@invitly.com',
      payerPhone: phone ? fullPhone(phone) : '+972500000000',
      total: {
        amount: { value: priceFormatted, currency: 'ILS' },
        label: productName
      }
    }
  }

  /* --------------------------------------
      HANDLE REGISTRATION
  -------------------------------------- */
  const handleCouponValidation = async () => {
    setCouponLoading(true)
    setCouponError('')
    setCouponValid(false)

    try {
      const response = await axios.post(`${BASE_URL}/validate-coupon`, {
        couponCode: couponCode,
      })

      if (response.data.valid) {
        setCouponValid(true)
        setCouponError('')
      } else {
        setCouponError('Invalid coupon code')
        setCouponValid(false)
      }
    } catch (error) {
      console.error('Error validating coupon:', error)
      setCouponError('Error validating coupon. Please try again.')
      setCouponValid(false)
    } finally {
      setCouponLoading(false)
    }
  }

  const handleCompleteRegistration = async () => {
    setLoading(true)
    setError('')

    try {
      // Parse full name into components
      const wife = wifeName.split(' ')
      const husband = husbandName.split(' ')

      // Create form data
      const formData = new FormData()
      formData.append('wife_firstname', wife[0])
      formData.append('wife_lastname', wife[1])
      formData.append('husband_firstname', husband[0])
      formData.append('husband_lastname', husband[1])
      formData.append('username', username)
      formData.append('email', email)
      formData.append('phone_number', fullPhone(phone))
      formData.append('event_date', eventDate)
      formData.append('event_address', mapLink)
      formData.append('password', password)
      formData.append('rsvp_text', rsvpText)
      formData.append('tomorrow_message', tomorrowMessage)
      formData.append('rsvp_second_text', rsvpTextSecond)
      formData.append('after_wedding_text', afterWeddingText)

      // Add transport data if organized
      formData.append('organize_transport', organizeTransport)
      if (organizeTransport && transportStops.length > 0) {
        formData.append('transport_stops', JSON.stringify(transportStops))
      }

      if (invitationImage) {
        formData.append('invitation', invitationImage)
      }

      const response = await axios.post(
        `${BASE_URL}/admin/register`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )

      if (response.status === 200) {
        // Show verification modal instead of alert
        setShowVerificationModal(true)
        setRemainingTime(60)
        setCanResend(false)
        sendCode() // Auto-send verification code
      }
    } catch (err) {
      console.error('Registration error:', err)
      
      // Parse error message from response
      let errorMessage = language === 'he' 
        ? 'ההרשמה נכשלה. אנא בדוק את הפרטים שלך.'
        : 'Registration failed. Please check your details.'
      
      if (err.response?.data) {
        const errorData = err.response.data
        const detail = errorData.msg?.detail || errorData.detail
        
        if (detail) {
          // Check for duplicate phone number
          if (detail.includes('phone_number') && detail.includes('already exists')) {
            errorMessage = language === 'he'
              ? 'מספר הטלפון הזה כבר רשום במערכת. אנא השתמש במספר טלפון אחר או התחבר לחשבון הקיים שלך.'
              : 'This phone number is already registered. Please use a different phone number or login to your existing account.'
          }
          // Check for duplicate email
          else if (detail.includes('email') && detail.includes('already exists')) {
            errorMessage = language === 'he'
              ? 'כתובת האימייל הזו כבר רשומה במערכת. אנא השתמש בכתובת אימייל אחרת או התחבר לחשבון הקיים שלך.'
              : 'This email address is already registered. Please use a different email or login to your existing account.'
          }
          // Check for duplicate username
          else if (detail.includes('username') && detail.includes('already exists')) {
            errorMessage = language === 'he'
              ? 'שם המשתמש הזה כבר תפוס. אנא בחר שם משתמש אחר.'
              : 'This username is already taken. Please choose a different username.'
          }
          // Generic duplicate key error
          else if (detail.includes('already exists')) {
            errorMessage = language === 'he'
              ? 'הפרטים שהזנת כבר קיימים במערכת. אנא בדוק את המידע שלך.'
              : 'The information you entered already exists in the system. Please check your details.'
          }
        }
        // Check for other specific error messages
        else if (errorData.error) {
          errorMessage = language === 'he'
            ? `שגיאה: ${errorData.error}`
            : `Error: ${errorData.error}`
        }
      }
      
      setError(errorMessage)
    }
    setLoading(false)
  }

  // Function to toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Function to send verification code
  const sendCode = async () => {
    setRemainingTime(60)
    setCanResend(false)

    try {
      await axios.post(`${BASE_URL}/admin/send-code`, { username })
      setError('')
      // Silently succeed - user already sees the verification modal
    } catch (err) {
      setError('Failed to send verification code. Please try again.')
      console.error('Error sending verification code:', err)
    }
  }

  // Handle verification code submission
  const handleVerificationSubmit = async (code) => {
    try {
      const response = await axios.post(`${BASE_URL}/admin/verify-code`, {
        username,
        code,
      })

      if (response.status === 200) {
        // Set verification success to true instead of closing modal
        setVerificationSuccess(true)
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.')
      console.error('Verification error:', err)
    }
  }

  // Navigate to dashboard
  const goToDashboard = () => {
    window.open('https://home.invitly.org/welcome', '_blank')
    setShowVerificationModal(false)
  }

  // Add transport stop function
  const addTransportStop = () => {
    if (newStop.trim()) {
      setTransportStops([...transportStops, newStop.trim()])
      setNewStop('')
    }
  }

  // Remove transport stop
  const removeTransportStop = (index) => {
    const updatedStops = [...transportStops]
    updatedStops.splice(index, 1)
    setTransportStops(updatedStops)
  }

  // Toggle transport organization
  const toggleTransport = (value) => {
    setOrganizeTransport(value)
    if (!value) {
      setTransportStops([])
    }
  }

  // Step 1 validation: required fields + email/phone uniqueness
  const handleStep1Next = async () => {
    const errors = {}
    const wifeWords = wifeName.trim().split(/\s+/)
    const husbandWords = husbandName.trim().split(/\s+/)

    if (!wifeName.trim()) errors.wifeName = language === 'he' ? 'שם הכלה נדרש' : 'Wife name is required'
    else if (wifeWords.length < 2) errors.wifeName = language === 'he' ? 'יש להזין שם פרטי ושם משפחה' : 'Please enter first and last name'

    if (!husbandName.trim()) errors.husbandName = language === 'he' ? 'שם החתן נדרש' : 'Husband name is required'
    else if (husbandWords.length < 2) errors.husbandName = language === 'he' ? 'יש להזין שם פרטי ושם משפחה' : 'Please enter first and last name'

    if (!email.trim()) {
      errors.email = language === 'he' ? 'אימייל נדרש' : 'Email is required'
    } else {
      const atIndex = email.indexOf('@')
      const hasValidFormat = atIndex > 0 && email.slice(atIndex + 1).includes('.')
      if (!hasValidFormat) errors.email = language === 'he' ? 'כתובת אימייל לא תקינה' : 'Invalid email address'
    }

    const phoneDigits = phone.trim().replace(/\D/g, '')
    if (!phone.trim()) {
      errors.phone = language === 'he' ? 'טלפון נדרש' : 'Phone is required'
    } else if (!phoneDigits.startsWith('5') || phoneDigits.length !== 9) {
      errors.phone = language === 'he' ? 'מספר הטלפון חייב להתחיל ב-5 ולהכיל 9 ספרות' : 'Phone must start with 5 and contain 9 digits'
    }

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setValidating(true)
    try {
      const response = await axios.post(`${BASE_URL}/check-availability`, { email, phone })
      const taken = response.data
      const dupeErrors = {}
      if (taken.email) dupeErrors.email = language === 'he' ? 'כתובת אימייל זו כבר רשומה' : 'This email is already registered'
      if (taken.phone) dupeErrors.phone = language === 'he' ? 'מספר טלפון זה כבר רשום' : 'This phone number is already registered'
      if (Object.keys(dupeErrors).length > 0) { setFieldErrors(dupeErrors); return }
    } catch {
      // network error — allow to proceed
    } finally {
      setValidating(false)
    }
    nextStep()
  }

  // Step 2 validation: required fields only
  const handleStep2Next = () => {
    const errors = {}
    if (!mapLink.trim()) errors.mapLink = language === 'he' ? 'כתובת האירוע נדרשת' : 'Event address is required'
    if (!eventDate) errors.eventDate = language === 'he' ? 'תאריך האירוע נדרש' : 'Event date is required'
    setFieldErrors(errors)
    if (Object.keys(errors).length === 0) nextStep()
  }

  // Step 5 validation: required fields + username uniqueness
  const handleStep5Next = async () => {
    const errors = {}
    if (!username.trim()) errors.username = language === 'he' ? 'שם משתמש נדרש' : 'Username is required'
    if (!password) errors.password = language === 'he' ? 'סיסמה נדרשת' : 'Password is required'
    if (!confirmPassword) errors.confirmPassword = language === 'he' ? 'אנא אשר את הסיסמה' : 'Please confirm your password'
    else if (password !== confirmPassword) errors.confirmPassword = language === 'he' ? 'הסיסמאות אינן תואמות' : 'Passwords do not match'

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setValidating(true)
    try {
      const response = await axios.post(`${BASE_URL}/check-availability`, { username })
      if (response.data.username) {
        setFieldErrors({ username: language === 'he' ? 'שם משתמש זה כבר תפוס' : 'This username is already taken' })
        return
      }
    } catch {
      // network error — allow to proceed
    } finally {
      setValidating(false)
    }
    nextStep()
  }

  return (
    <div className="app-container">
      <Header />
   
      <div className="register-container">
        <div className="step-indicator-wrapper">
          <p className="step-progress-text">
            {language === 'he' ? 'שלב' : 'Step'}{' '}
            <span>{step}</span>{' '}
            {language === 'he' ? 'מתוך' : 'of'} 6
          </p>
          <div className="step-indicator">
            {[1,2,3,4,5,6].map(n => (
              <React.Fragment key={n}>
                {n > 1 && <div className={`step-line${step > n - 1 ? ' completed' : ''}`} />}
                <div className="step-dot-wrapper">
                  <div className={`step-dot${step === n ? ' active' : ''}${step > n ? ' completed' : ''}`}>
                    {step > n ? '✓' : n}
                  </div>
                  {(n === 3 || n === 4) && (
                    <span className="step-optional-label">
                      {language === 'he' ? 'אופציונלי' : 'optional'}
                    </span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

          {step === 1 && (
            <div className="register-card">
              <h2>
                <T k="signUpTitle" />
              </h2>
            <p>
              <T k="signUpSubtitle" />
            </p>

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.wifeName
                  : translations.en.wifeName
              }
              type="text"
              placeholder={
                language === 'he'
                  ? translations.he.wifeName
                  : translations.en.wifeName
              }
              value={wifeName}
              onChange={(e) => { setWifeName(e.target.value); clearFieldError('wifeName') }}
              tooltip={
                language === 'he'
                  ? translations.he.wifeNameTooltip
                  : translations.en.wifeNameTooltip
              }
              error={fieldErrors.wifeName}
            />
            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.husbandName
                  : translations.en.husbandName
              }
              type="text"
              placeholder={
                language === 'he'
                  ? translations.he.husbandName
                  : translations.en.husbandName
              }
              value={husbandName}
              onChange={(e) => { setHusbandName(e.target.value); clearFieldError('husbandName') }}
              tooltip={
                language === 'he'
                  ? translations.he.husbandNameTooltip
                  : translations.en.husbandNameTooltip
              }
              error={fieldErrors.husbandName}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.email
                  : translations.en.email
              }
              type="email"
              placeholder={
                language === 'he'
                  ? translations.he.email
                  : translations.en.email
              }
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearFieldError('email') }}
              tooltip={
                language === 'he'
                  ? translations.he.emailTooltip
                  : translations.en.emailTooltip
              }
              error={fieldErrors.email}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.phoneNumber
                  : translations.en.phoneNumber
              }
              type="tel"
              placeholder={
                language === 'he'
                  ? translations.he.phoneNumber
                  : translations.en.phoneNumber
              }
              value={phone}
              onChange={(e) => { setPhone(e.target.value); clearFieldError('phone') }}
              prefix="+972"
              error={fieldErrors.phone}
            />

            <div className="input-tooltip-wrapper">
              <label className="input-label">
                {language === 'he' ? 'סוג אירוע' : 'Event Type'}
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              >
                <option value="Wedding">
                  {language === 'he'
                    ? translations.he.wedding
                    : translations.en.wedding}
                </option>
                <option value="Birthday">
                  {language === 'he'
                    ? translations.he.birthday
                    : translations.en.birthday}
                </option>
                <option value="Other">
                  {language === 'he'
                    ? translations.he.other
                    : translations.en.other}
                </option>
              </select>
            </div>

            {/* Display error message if there is one */}
            {error && <div className="error-message">{error}</div>}

            <button
              className="next-btn"
              onClick={handleStep1Next}
              disabled={validating}
            >
              {validating ? (language === 'he' ? 'בודק...' : 'Checking...') : <T k="nextStep" />}
            </button>
          </div>
             )}

          {step === 2 && (
          <div className="register-card">
            <h2>
              <T k="eventDetails" />
            </h2>

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.googleMapLink
                  : translations.en.googleMapLink
              }
              type="text"
              placeholder={
                language === 'he'
                  ? translations.he.googleMapLink
                  : translations.en.googleMapLink
              }
              value={mapLink}
              onChange={(e) => { setMapLink(e.target.value); clearFieldError('mapLink') }}
              tooltip={
                language === 'he'
                  ? translations.he.googleMapTooltip
                  : translations.en.googleMapTooltip
              }
              error={fieldErrors.mapLink}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.eventDate
                  : translations.en.eventDate
              }
              type="date"
              placeholder={
                language === 'he'
                  ? translations.he.eventDate
                  : translations.en.eventDate
              }
              value={eventDate}
              onChange={(e) => { setEventDate(e.target.value); clearFieldError('eventDate') }}
              tooltip={
                language === 'he'
                  ? translations.he.eventDateTooltip
                  : translations.en.eventDateTooltip
              }
              min={new Date().toISOString().split('T')[0]}
              error={fieldErrors.eventDate}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.eventTime
                  : translations.en.eventTime
              }
              type="time"
              placeholder={
                language === 'he'
                  ? translations.he.eventTime
                  : translations.en.eventTime
              }
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.eventTimeTooltip
                  : translations.en.eventTimeTooltip
              }
            />

            <div className="input-tooltip-wrapper">
              <div className="label-tooltip">
                <label htmlFor="invitationImage" className="upload-label">
                  <FaUpload className="upload-icon" />
                  {language === 'he'
                    ? translations.he.uploadInvitation
                    : translations.en.uploadInvitation}
                  <span className="optional-tag">
                    {language === 'he' ? '(אופציונלי)' : '(optional)'}
                  </span>
                </label>
                <input
                  id="invitationImage"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (file) {
                      console.log('Selected file:', file)
                      console.log('File size:', file.size)
                      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
                      if (file.size > maxSize) {
                        setImageUploadError(
                          language === 'he' 
                            ? 'גודל הקובץ חייב להיות מתחת ל-10 מגה-בייט'
                            : 'File size must be under 10 MB'
                        )
                        e.target.value = '' // Clear the input
                        setInvitationImage(null)
                      } else {
                        setImageUploadError('') // Clear any previous errors
                        setInvitationImage(file)
                      }
                    }
                  }}
                  className="upload-input"
                />
                <div className="tooltip-container">
                  <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    {language === 'he'
                      ? translations.he.uploadInvitationTooltip
                      : translations.en.uploadInvitationTooltip}
                  </span>
                </div>
              </div>
              {invitationImage && (
                <div className="file-name-display">
                  {language === 'he'
                    ? translations.he.selected
                    : translations.en.selected}
                  : {invitationImage.name}
                </div>
              )}
              {imageUploadError && (
                <div className="error-message">
                  {imageUploadError}
                </div>
              )}
            </div>

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <button
                className="next-btn"
                onClick={handleStep2Next}
              >
                <T k="nextStep" />
              </button>
            </div>
          </div>
             )}
        {step === 3 && (
          <div className="register-card">
            <div className="card-title-row">
              <h2><T k="customizeMessages" /></h2>
              <span className="optional-badge">
                {language === 'he' ? 'אופציונלי' : 'Optional'}
              </span>
            </div>
            <p className="optional-note">
              {language === 'he'
                ? 'ניתן למלא ולערוך הודעות אלה מאוחר יותר מהדשבורד שלך.'
                : 'You can fill in and edit these messages later from your dashboard.'}
            </p>

            <div className="message-input-container">
              <label className="input-label">
                {language === 'he'
                  ? translations.he.firstRsvpMessage
                  : translations.en.firstRsvpMessage}
              </label>
              <div className="input-tooltip-wrapper">
                <div className="label-tooltip">
                  <textarea
                    placeholder={
                      language === 'he'
                        ? 'הודעת RSVP ראשונה...'
                        : 'First RSVP SMS...'
                    }
                    value={rsvpText}
                    onChange={(e) => setRsvpText(e.target.value)}
                    aria-label={
                      language === 'he'
                        ? translations.he.firstRsvpMessage
                        : translations.en.firstRsvpMessage
                    }
                  />
                  <div className="tooltip-container">
                    <FaInfoCircle className="info-icon" />
                    <span className="tooltip-text">
                      {language === 'he'
                        ? translations.he.firstRsvpTooltip
                        : translations.en.firstRsvpTooltip}
                    </span>
                  </div>
                </div>
              </div>
            </div>


            <TextareaWithTooltip
              label={
                language === 'he'
                  ? translations.he.reminderRsvpMessage
                  : translations.en.reminderRsvpMessage
              }
              placeholder={
                language === 'he'
                  ? 'הודעת תזכורת לאורחים שלא ענו...'
                  : 'Reminder SMS for unresponsive guests...'
              }
              value={rsvpTextSecond}
              onChange={(e) => setRsvpTextSecond(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.reminderRsvpTooltip
                  : translations.en.reminderRsvpTooltip
              }
            />

                        <TextareaWithTooltip
              label={
                language === 'he'
                  ? translations.he.tomorrowMessage
                  : translations.en.tomorrowMessage
              }
              placeholder={
                language === 'he'
                  ? 'הודעת תזכורת יום לפני האירוע...'
                  : 'Reminder SMS the day before the event...'
              }
              value={tomorrowMessage}
              onChange={(e) => setTomorrowMessage(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.tomorrowTooltip
                  : translations.en.tomorrowTooltip
              }
            />

            <TextareaWithTooltip
              label={
                language === 'he'
                  ? translations.he.afterWeddingMessage
                  : translations.en.afterWeddingMessage
              }
              placeholder={
                language === 'he' ? 'הודעת תודה...' : 'Thank-you SMS...'
              }
              value={afterWeddingText}
              onChange={(e) => setAfterWeddingText(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.afterWeddingTooltip
                  : translations.en.afterWeddingTooltip
              }
            />

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <div className="next-group">
                <button className="skip-btn" onClick={nextStep}>
                  {language === 'he' ? 'דלג' : 'Skip'}
                </button>
                <button className="next-btn" onClick={nextStep}>
                  <T k="nextStep" />
                </button>
              </div>
            </div>
          </div>
           )}


        {step === 4 && (
          <div className="register-card">
            <div className="card-title-row">
              <h2>{language === 'he' ? 'תחבורה מאורגנת' : 'Organized Transport'}</h2>
              <span className="optional-badge">
                {language === 'he' ? 'אופציונלי' : 'Optional'}
              </span>
            </div>
            <p className="optional-note">
              {language === 'he'
                ? 'ניתן להוסיף ולערוך הסעות מאוחר יותר מהדשבורד שלך.'
                : 'You can add and edit transport stops later from your dashboard.'}
            </p>
            <p>
              {language === 'he'
                ? 'האם תרצה/י לארגן הסעה מאורגנת לאורחים?'
                : 'Would you like to organize transport for guests?'}
            </p>

            <div className="transport-options">
              <button
                className={organizeTransport ? 'active' : ''}
                onClick={() => toggleTransport(true)}
              >
                {language === 'he' ? 'כן' : 'Yes'}
              </button>
              <button
                className={!organizeTransport ? 'active' : ''}
                onClick={() => toggleTransport(false)}
              >
                {language === 'he' ? 'לא' : 'No'}
              </button>
            </div>

            {organizeTransport && (
              <div className="transport-stops-section">
                <p>
                  {language === 'he'
                    ? 'הוסף תחנות עצירה:'
                    : 'Add transport stops:'}
                </p>
                <div className="add-stop-container">
                  <input
                    type="text"
                    value={newStop}
                    onChange={(e) => setNewStop(e.target.value)}
                    placeholder={
                      language === 'he'
                        ? 'הקלד את שם התחנה...'
                        : 'Type stop name...'
                    }
                  />
                  <button className="add-stop-btn" onClick={addTransportStop}>
                    {language === 'he' ? 'הוסף' : 'Add'}
                  </button>
                </div>

                <ul className="stops-list">
                  {transportStops.map((stop, index) => (
                    <li key={index} className="stop-item">
                      <span>{stop}</span>
                      <button
                        className="remove-stop-btn"
                        onClick={() => removeTransportStop(index)}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <div className="next-group">
                <button className="skip-btn" onClick={nextStep}>
                  {language === 'he' ? 'דלג' : 'Skip'}
                </button>
                <button className="next-btn" onClick={nextStep}>
                  <T k="nextStep" />
                </button>
              </div>
            </div>
          </div>
             )}

        {step === 5 && (
          <div className="register-card">
            <h2>
              <T k="createAccount" />
            </h2>
            <p>
              <T k="accountSetup" />
            </p>

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.username
                  : translations.en.username
              }
              type="text"
              placeholder={
                language === 'he'
                  ? translations.he.username
                  : translations.en.username
              }
              value={username}
              onChange={(e) => { setUsername(e.target.value); clearFieldError('username') }}
              tooltip={
                language === 'he'
                  ? translations.he.usernameTooltip
                  : translations.en.usernameTooltip
              }
              error={fieldErrors.username}
            />

            <div className="input-tooltip-wrapper">
              <div className="label-tooltip password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={
                    language === 'he'
                      ? translations.he.password
                      : translations.en.password
                  }
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearFieldError('password') }}
                  aria-label={
                    language === 'he'
                      ? translations.he.password
                      : translations.en.password
                  }
                />
                <div
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
                <div className="tooltip-container">
                  <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    {language === 'he'
                      ? translations.he.passwordTooltip
                      : translations.en.passwordTooltip}
                  </span>
                </div>
              </div>
              {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
            </div>

            <div className="input-tooltip-wrapper">
              <div className="label-tooltip password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={
                    language === 'he'
                      ? translations.he.confirmPassword
                      : translations.en.confirmPassword
                  }
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword') }}
                  aria-label={
                    language === 'he'
                      ? translations.he.confirmPassword
                      : translations.en.confirmPassword
                  }
                />
                <div
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
                <div className="tooltip-container">
                  <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    {language === 'he'
                      ? translations.he.confirmPasswordTooltip
                      : translations.en.confirmPasswordTooltip}
                  </span>
                </div>
              </div>
              {fieldErrors.confirmPassword && <span className="field-error">{fieldErrors.confirmPassword}</span>}
            </div>

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <button
                className="next-btn"
                onClick={handleStep5Next}
                disabled={validating}
              >
                {validating ? (language === 'he' ? 'בודק...' : 'Checking...') : <T k="nextStep" />}
              </button>
            </div>
          </div>       
           )}

            
        {step === 6 && (
          <div className="register-card">
            <h2>
              <T k="paymentTitle" />
            </h2>

            {/* Plan Selection Section */}
            {!selectedPlan && (
              <div className="plan-selection-section">
                <p className="plan-selection-title">
                  {language === 'he' ? 'בחר תוכנית:' : 'Choose your plan:'}
                </p>
                <div className="plan-cards">
                  {/* Basic Plan */}
                  <div 
                    className={`plan-card ${selectedPlan === 'basic' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('basic')}
                  >
                    <h3><T k="basicPlan" /></h3>
                    <p className="plan-price"><T k="basicPrice" /></p>
                    <ul className="plan-features">
                      <li><T k="basicFeature1" /></li>
                      <li><T k="basicFeature2" /></li>
                      <li><T k="basicFeature3" /></li>
                    </ul>
                    <button 
                      className="select-plan-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedPlan('basic')
                      }}
                    >
                      <T k="choosePlan" />
                    </button>
                  </div>

                  {/* Premium Plan */}
                  <div 
                    className={`plan-card featured ${selectedPlan === 'premium' ? 'selected' : ''}`}
                    onClick={() => setSelectedPlan('premium')}
                  >
                    <h3><T k="premiumPlan" /></h3>
                    <p className="plan-price"><T k="premiumPrice" /></p>
                    <ul className="plan-features">
                      <li><T k="premiumFeature1" /></li>
                      <li><T k="premiumFeature2" /></li>
                      <li><T k="premiumFeature3" /></li>
                    </ul>
                    <button 
                      className="select-plan-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedPlan('premium')
                      }}
                    >
                      <T k="choosePlan" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Selected - Show Payment Options */}
            {selectedPlan && (
              <div className="payment-choice-section">
                <div className="selected-plan-info">
                  <p>
                    {language === 'he' ? 'תוכנית נבחרה: ' : 'Selected plan: '}
                    <strong>
                      {selectedPlan === 'basic' 
                        ? (language === 'he' ? 'בסיסי' : 'Basic')
                        : (language === 'he' ? 'פרימיום' : 'Premium')
                      }
                    </strong>
                    {' - '}
                    {selectedPlan === 'basic' 
                      ? '₪229'
                      : '₪259'
                    }
                  </p>
                  <button 
                    className="change-plan-btn"
                    onClick={() => {
                      setSelectedPlan('')
                      setPaymentChoice('')
                      setCouponValid(false)
                      setCouponCode('')
                      setCouponError('')
                    }}
                  >
                    {language === 'he' ? 'שנה תוכנית' : 'Change Plan'}
                  </button>
                </div>

                <p className="payment-choice-title">
                  {language === 'he' ? 'בחר אמצעי תשלום:' : 'Choose payment method:'}
                </p>
                <div className="payment-options">
                  <button 
                    className={`payment-option-btn coupon-option ${paymentChoice === 'coupon' ? 'selected' : ''}`}
                    onClick={() => {
                      setPaymentChoice('coupon')
                      // Reset card-related state if switching from card
                    }}
                  >
                    {language === 'he' ? 'יש לי קופון' : 'I have a coupon'}
                  </button>
                  <button 
                  // remove to enable card payments
                  disabled={true}
                    className={`payment-option-btn card-option ${paymentChoice === 'card' ? 'selected' : ''}`}
                    onClick={() => {
                      setPaymentChoice('card')
                      // Reset coupon-related state if switching from coupon
                      setCouponValid(false)
                      setCouponCode('')
                      setCouponError('')
                    }}
                  >
                    {language === 'he' ? 'תשלום בכרטיס אשראי' : 'Pay with Credit Card'}
                  </button>
                </div>

                {/* Coupon Payment Option */}
                {paymentChoice === 'coupon' && (
                  <div className="coupon-payment-section">
                    <div className="coupon-section">
                      <p className="coupon-label">
                        {language === 'he' ? 'קוד קופון' : 'Coupon Code'}
                      </p>
                      <div className="coupon-input-container">
                        <input
                          type="text"
                          placeholder={translations[language]?.couponCode || 'Coupon Code'}
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value)
                            setCouponValid(false)
                            setCouponError('')
                          }}
                          className="coupon-input"
                          aria-label={translations[language]?.couponCode || 'Coupon Code'}
                        />
                        <button
                          className="validate-coupon-btn"
                          onClick={handleCouponValidation}
                          disabled={!couponCode || couponLoading}
                        >
                          {couponLoading ? <T k="validating" /> : <T k="validateCoupon" />}
                        </button>
                      </div>

                      {couponError && (
                        <div className="coupon-error-message">
                          {couponError}
                        </div>
                      )}
                      {couponValid && (
                        <div className="coupon-success-message">
                          {language === 'he'
                            ? 'הקופון תקף! בזכות הקופון, השירות יעלה לך רק ₪50.'
                            : 'Coupon applied! Thanks to your coupon, the service will cost you only ₪50.'}
                        </div>
                      )}
                    </div>

                    {couponValid && (
                      <div className="card-payment-section">
                        {error && <div className="error-message">{error}</div>}
                        <PayMe3DSCheckout
                          saleDetails={buildCouponSaleInfo()}
                          onSuccess={handleCompleteRegistration}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Credit Card Payment Option */}
                {paymentChoice === 'card' && (
                  <div className="card-payment-section">
                    <PayMe3DSCheckout 
                      saleDetails={buildSaleInfo()}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Payment Error */}
            {paymentError && (
              <div className="error-message">
                {paymentError}
              </div>
            )}
            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
            </div>
          </div>
        )}

        {/* Verification Modal */}
        {showVerificationModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              {!verificationSuccess ? (
                <>
                  <h2>
                    <T k="confirmationCode" />
                  </h2>
                  <p>
                    <T k="codeSentTo" /> {phone}
                  </p>

                  {error && <div className="error-message">{error}</div>}

                  <VerificationCodeInput
                    length={6}
                    onComplete={handleVerificationSubmit}
                  />

                  <div className="resend-container">
                    {canResend ? (
                      <p>
                        <T k="didntReceiveCode" />{' '}
                        <button className="resend-link" onClick={sendCode}>
                          <T k="clickHere" />
                        </button>{' '}
                        {language === 'he' ? 'לשליחה חוזרת.' : 'to send again.'}
                      </p>
                    ) : (
                      <p>
                        <T k="resendCode" />{' '}
                        <span className="countdown">{remainingTime}</span>{' '}
                        <T k="seconds" />
                      </p>
                    )}
                  </div>

                  <button
                    className="close-modal-btn"
                    onClick={() => setShowVerificationModal(false)}
                  >
                    <T k="cancel" />
                  </button>
                </>
              ) : (
                /* Success message and dashboard link */
                <div className="success-message-container">
                  <h2>
                    <T k="registrationSuccess" />
                  </h2>
                  <p>
                    <T k="registrationSuccessMessage" />
                  </p>
                  <button
                    className="dashboard-link-btn"
                    onClick={goToDashboard}
                  >
                    <T k="goToDashboard" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
         
      </div>
    </div>
  )
}

export default Register
